/**
 * API Client
 *
 * Handles all HTTP requests to the backend API.
 * Automatically includes JWT token in Authorization header.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api'

class ApiClient {
  constructor() {
    this.token = null
  }

  /**
   * Set the JWT token for authenticated requests
   */
  setToken(token) {
    this.token = token
  }

  /**
   * Get request headers
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    return headers
  }

  /**
   * Handle API response
   */
  async handleResponse(response) {
    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const error = new Error(data.message || data.error || 'Request failed')
      error.status = response.status
      error.data = data
      throw error
    }

    return data
  }

  /**
   * GET request
   */
  async get(endpoint) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders()
    })

    return this.handleResponse(response)
  }

  /**
   * POST request
   */
  async post(endpoint, body = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    })

    return this.handleResponse(response)
  }

  /**
   * PUT request
   */
  async put(endpoint, body = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    })

    return this.handleResponse(response)
  }

  /**
   * PATCH request
   */
  async patch(endpoint, body = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    })

    return this.handleResponse(response)
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    })

    return this.handleResponse(response)
  }
}

// Export singleton instance
export const api = new ApiClient()
